import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import useThread from '../../libs/hooks/useThread';
// Remove headless UI import as it might not be installed
// import { Dialog } from '@headlessui/react';

const ThreadView = ({ reportId }) => {
  const {
    messages,
    loading,
    error,
    isReplyOpen,
    replyText,
    submitting,
    messagesEndRef,
    setIsReplyOpen,
    setReplyText,
    fetchMessages,
    submitReply,
    formatDate,
  } = useThread(reportId);

  const { currentUser, userClaims } = useAuth();

  // Determine if message is from current user
  const isCurrentUser = senderId => {
    return senderId === currentUser?.uid;
  };

  // Get display name for sender
  const getSenderDisplayName = message => {
    if (isCurrentUser(message.senderId)) {
      return 'You';
    }

    if (message.senderRole === 'dealer_admin') {
      return 'Dealer';
    }

    if (message.senderRole === 'admin' || message.senderRole === 'superadmin') {
      return 'Signal Bridge';
    }

    return message.senderName || message.senderRole || 'User';
  };

  // Check if user has permission to reply
  const canReply = () => {
    // Get role from claims or firestore data
    const role = userClaims?.role || currentUser?.role;
    console.log('[THREAD-VIEW] User role for reply permission:', role);
    // Allow admin, demo_user, tenant_admin, or any authenticated user to reply
    return role === 'admin' || role === 'demo_user' || role === 'tenant_admin' || !!currentUser;
  };

  // Handle form submission
  const handleSubmit = e => {
    e.preventDefault();
    submitReply();
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden mt-6">
      <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-800">Thread</h3>
        <button onClick={fetchMessages} className="text-blue-600 hover:text-blue-800 text-sm">
          Refresh
        </button>
      </div>

      {/* Messages container */}
      <div className="flex-1 p-4 overflow-y-auto bg-gray-50" style={{ maxHeight: '400px' }}>
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="text-center text-gray-500 py-10">
            <p>Unable to load messages. You can still send a reply below.</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500 py-10">
            <p>No messages in this thread yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message, index) => (
              <div
                key={message.id || index}
                className={`flex ${isCurrentUser(message.senderId) ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-3/4 rounded-lg p-3 ${
                    isCurrentUser(message.senderId)
                      ? 'bg-blue-100 text-blue-900'
                      : message.senderRole === 'admin'
                        ? 'bg-indigo-100 text-indigo-900'
                        : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-semibold text-sm">{getSenderDisplayName(message)}</span>
                    <span className="text-xs text-gray-500 ml-2">
                      {formatDate(message.createdAt)}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{message.message}</p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Reply section - Always show this section */}
      <div className="p-4 bg-white border-t border-gray-200">
        {isReplyOpen ? (
          <form onSubmit={handleSubmit}>
            <textarea
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder="Type your reply..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
              disabled={submitting}
            />
            <div className="flex justify-end mt-2 space-x-2">
              <button
                type="button"
                onClick={() => setIsReplyOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  submitting ? 'opacity-75 cursor-not-allowed' : ''
                }`}
                disabled={submitting}
              >
                {submitting ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </form>
        ) : (
          <button
            onClick={() => setIsReplyOpen(true)}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Reply
          </button>
        )}
      </div>

      {/* We're using the inline form approach instead of a modal */}
    </div>
  );
};

export default ThreadView;
